import api from '@/utils/api';

interface AiSensyCredentials {
  apiKey: string;
  userName: string;
}

interface AiSensyConfig {
  wabaId?: string;
  [key: string]: any;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

interface Template {
  id: string;
  element_name: string;
  status: string;
  category: string;
  language: string;
  parameters: any[];
  components: any[];
}

interface TriggerRule {
  id?: string;
  trigger_name: string;
  trigger_event: string;
  template_id: string;
  trigger_conditions: any;
  parameter_mapping: Record<string, string>;
  channel: string;
  is_active: boolean;
  priority?: number;
}

interface MessageLog {
  id: string;
  timestamp: string;
  template: string;
  recipient: string;
  status: string;
  message: string;
}

interface WABA {
  id: string;
  name: string;
  account_review_status: string;
  business_id: string;
  business_name?: string;
  ownership_type?: 'owned' | 'client';
}

interface Business {
  id: string;
  name: string;
  verification_status?: string;
}

class AiSensyService {
  private readonly baseUrl = '/integrations/aisensy';
  private readonly facebookOAuthUrl = '/facebook-oauth';

  /**
   * Get AiSensy integration settings
   */
  async getSettings(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/settings`,
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error getting AiSensy settings:', error);
      throw error;
    }
  }

  /**
   * Save AiSensy integration settings
   */
  async saveSettings(
    credentials: AiSensyCredentials,
    config: AiSensyConfig = {},
    isActive: boolean = false
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/settings`,
        method: api.Method.POST,
        body: {
          credentials,
          config,
          is_active: isActive
        }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error saving AiSensy settings:', error);
      throw error;
    }
  }

  /**
   * Test AiSensy connection
   */
  async testConnection(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/test`,
        method: api.Method.POST,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error testing AiSensy connection:', error);
      throw error;
    }
  }

  /**
   * Get templates from AiSensy (via Facebook Graph API)
   */
  async getTemplates(): Promise<ApiResponse<Template[]>> {
    try {
      const response = await api.req<Template[]>({
        url: `${this.baseUrl}/templates`,
        method: api.Method.GET,
      });
      return response as ApiResponse<Template[]>;
    } catch (error) {
      console.error('Error getting AiSensy templates:', error);
      throw error;
    }
  }

  /**
   * Sync templates from Facebook Graph API
   */
  async syncTemplates(): Promise<ApiResponse<Template[]>> {
    try {
      const response = await api.req<Template[]>({
        url: `${this.baseUrl}/sync-templates`,
        method: api.Method.POST,
      });
      return response as ApiResponse<Template[]>;
    } catch (error) {
      console.error('Error syncing AiSensy templates:', error);
      throw error;
    }
  }

  /**
   * Get automation triggers
   */
  async getTriggers(): Promise<ApiResponse<TriggerRule[]>> {
    try {
      const response = await api.req<TriggerRule[]>({
        url: '/integrations/aisensy/triggers',
        method: api.Method.GET,
      });
      return response as ApiResponse<TriggerRule[]>;
    } catch (error) {
      console.error('Error getting AiSensy triggers:', error);
      throw error;
    }
  }

  /**
   * Save automation trigger
   */
  async saveTrigger(trigger: TriggerRule): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: '/integrations/aisensy/triggers',
        method: api.Method.POST,
        body: trigger
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error saving AiSensy trigger:', error);
      throw error;
    }
  }

  /**
   * Create automation trigger
   */
  async createTrigger(trigger: TriggerRule): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: '/integrations/aisensy/triggers',
        method: api.Method.POST,
        body: trigger
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error creating AiSensy trigger:', error);
      throw error;
    }
  }

  /**
   * Update automation trigger
   */
  async updateTrigger(triggerId: string, trigger: TriggerRule): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: '/integrations/aisensy/triggers',
        method: api.Method.POST,
        body: { ...trigger, id: triggerId }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error updating AiSensy trigger:', error);
      throw error;
    }
  }

  /**
   * Delete automation trigger
   */
  async deleteTrigger(triggerId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `/integrations/aisensy/triggers/${triggerId}`,
        method: api.Method.DELETE,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error deleting AiSensy trigger:', error);
      throw error;
    }
  }

  /**
   * Toggle trigger status
   */
  async toggleTriggerStatus(triggerId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/triggers/${triggerId}/toggle`,
        method: api.Method.PATCH,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error toggling AiSensy trigger status:', error);
      throw error;
    }
  }

  /**
   * Get message logs
   */
  async getLogs(status: string = 'all'): Promise<ApiResponse<{ messages: MessageLog[]; total: number }>> {
    try {
      const response = await api.req<{ messages: MessageLog[]; total: number }>({
        url: `${this.baseUrl}/logs?status=${status}`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{ messages: MessageLog[]; total: number }>;
    } catch (error) {
      console.error('Error getting AiSensy logs:', error);
      throw error;
    }
  }

  /**
   * Send test message
   */
  async sendTestMessage(templateId: string, recipient: string, parameters: Record<string, string>): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.baseUrl}/test-message`,
        method: api.Method.POST,
        body: {
          template_id: templateId,
          recipient,
          parameters
        }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error sending AiSensy test message:', error);
      throw error;
    }
  }

  /**
   * Get CRM lead columns for parameter mapping
   */
  async getCrmLeadColumns(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: '/integrations/crm/lead-columns',
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error getting CRM lead columns:', error);
      throw error;
    }
  }

  /**
   * Get status options for triggers
   */
  async getStatusOptions(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: '/integrations/crm/status-options',
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error getting status options:', error);
      throw error;
    }
  }

  /**
   * Save parameter mapping for a template (template-level default mapping)
   */
  async saveTemplateParameterMapping(templateId: string, parameterMapping: Record<string, string>): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `/integrations/templates/${templateId}/parameter-mapping`,
        method: api.Method.PATCH,
        body: { parameter_mapping: parameterMapping }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error saving template parameter mapping:', error);
      throw error;
    }
  }

  // Facebook OAuth Methods

  /**
   * Generate Facebook OAuth URL
   */
  async generateFacebookOAuthUrl(redirectUri: string): Promise<ApiResponse<{ oauth_url: string; state: string }>> {
    try {
      const response = await api.req<{ oauth_url: string; state: string }>({
        url: `${this.facebookOAuthUrl}/connect?redirect_uri=${encodeURIComponent(redirectUri)}`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{ oauth_url: string; state: string }>;
    } catch (error) {
      console.error('Error generating Facebook OAuth URL:', error);
      throw error;
    }
  }

  /**
   * Handle Facebook OAuth callback
   */
  async handleFacebookCallback(code: string, redirectUri: string, wabaId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.facebookOAuthUrl}/callback`,
        method: api.Method.POST,
        body: {
          code,
          redirect_uri: redirectUri,
          waba_id: wabaId
        }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error handling Facebook OAuth callback:', error);
      throw error;
    }
  }

  /**
   * Get user's Facebook Business accounts (Step 2)
   */
  async getBusinesses(): Promise<ApiResponse<{ data: Business[]; selected_business: { id: string; name: string } | null }>> {
    try {
      const response = await api.req<{ data: Business[]; selected_business: { id: string; name: string } | null }>({
        url: `${this.facebookOAuthUrl}/businesses`,
        method: api.Method.GET,
      });
      return response as ApiResponse<{ data: Business[]; selected_business: { id: string; name: string } | null }>;
    } catch (error) {
      console.error('Error getting businesses:', error);
      throw error;
    }
  }

  /**
   * Select Facebook Business account (Step 2)
   */
  async selectBusiness(businessId: string, businessName: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.facebookOAuthUrl}/select-business`,
        method: api.Method.POST,
        body: { business_id: businessId, business_name: businessName }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error selecting business:', error);
      throw error;
    }
  }

  /**
   * Get WABAs for the selected business (Step 3)
   */
  async getWABAs(businessId?: string): Promise<ApiResponse<{ data: WABA[]; business_id: string; business_name: string; selected_waba: { id: string; name: string } | null }>> {
    try {
      const url = businessId
        ? `${this.facebookOAuthUrl}/wabas?business_id=${businessId}`
        : `${this.facebookOAuthUrl}/wabas`;
      const response = await api.req<{ data: WABA[]; business_id: string; business_name: string; selected_waba: { id: string; name: string } | null }>({
        url,
        method: api.Method.GET,
      });
      return response as ApiResponse<{ data: WABA[]; business_id: string; business_name: string; selected_waba: { id: string; name: string } | null }>;
    } catch (error) {
      console.error('Error getting WABAs:', error);
      throw error;
    }
  }

  /**
   * Select WhatsApp Business Account (Step 3)
   */
  async selectWABA(wabaId: string, wabaName?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.facebookOAuthUrl}/select-waba`,
        method: api.Method.POST,
        body: { waba_id: wabaId, waba_name: wabaName }
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error selecting WABA:', error);
      throw error;
    }
  }

  /**
   * Disconnect Facebook account
   */
  async disconnectFacebook(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.facebookOAuthUrl}/disconnect`,
        method: api.Method.POST,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error disconnecting Facebook:', error);
      throw error;
    }
  }

  /**
   * Check Facebook permissions
   */
  async checkFacebookPermissions(): Promise<ApiResponse<any>> {
    try {
      const response = await api.req<any>({
        url: `${this.facebookOAuthUrl}/permissions`,
        method: api.Method.GET,
      });
      return response as ApiResponse<any>;
    } catch (error) {
      console.error('Error checking Facebook permissions:', error);
      throw error;
    }
  }
}

const aiSensyService = new AiSensyService();
export default aiSensyService;